import { apiClient } from './client'
import type { MetaRecette } from '@/types/recipe'
import {
  isChiefOfStaffDeployment,
  readInstanceRole,
} from '@/utils/instanceRole'
import type {
  CompanyMemoryStatus,
  LLMProvider,
  MCPSSEServer,
  MCPSTDIOServer,
  RegistryLLMModel,
  RegistrySkill,
  StorageCapabilities,
  StorageType,
  UpdateInternalMCPRequest,
} from './runtimeTypes'

export type DeploymentSnapshotImportMode = 'create' | 'overwrite'

export interface DeploymentSnapshotEnvelope {
  schema_version: string
  exported_at?: string
  source?: Record<string, unknown>
  kdf?: Record<string, unknown>
  cipher?: Record<string, unknown>
  payload?: string
  [key: string]: unknown
}

export interface DeploymentSnapshotExportRequest {
  passphrase: string
  include_registry_definitions?: boolean
}

export interface DeploymentSnapshotImportRequest {
  passphrase: string
  snapshot: DeploymentSnapshotEnvelope
  mode?: DeploymentSnapshotImportMode
  target_deployment_id?: string
  dry_run?: boolean
  launch_after_import?: boolean
  redeploy_after_import?: boolean
  allow_registry_auto_create?: boolean
}

export interface DeploymentSnapshotImportResult {
  mode: DeploymentSnapshotImportMode
  dry_run: boolean
  target_deployment_id?: string
  deployment_id?: string
  created?: string[]
  updated?: string[]
  skipped?: string[]
  warnings?: string[]
  errors?: string[]
  summary?: Record<string, unknown>
}

export interface DeploymentEdges {
  llmprovider?: LLMProvider[]
  llmmodel?: RegistryLLMModel | null
  mcpsseserver?: MCPSSEServer[]
  mcpstdioserver?: MCPSTDIOServer[]
  skill?: RegistrySkill[]
  skills?: RegistrySkill[]
}

export type DeploymentKind =
  | 'sandbox'
  | 'development'
  | 'integration_test'
  | 'staging'
  | 'production'
  | 'unknown'

export const DEPLOYMENT_KINDS: DeploymentKind[] = [
  'sandbox',
  'development',
  'integration_test',
  'staging',
  'production',
]

export function normalizeDeploymentKind(value: unknown): DeploymentKind {
  switch (String(value ?? '').trim().toLowerCase()) {
    case 'sandbox':
      return 'sandbox'
    case 'development':
    case 'dev':
      return 'development'
    case 'integration_test':
    case 'integration-test':
      return 'integration_test'
    case 'staging':
    case 'preproduction':
    case 'pre-production':
      return 'staging'
    case 'production':
    case 'prod':
      return 'production'
    default:
      return 'unknown'
  }
}

export interface Deployment {
  id: string
  name: string
  orgId: string
  runtimeTarget?: 'justemaurice' | 'vm' | 'k8s'
  status: 'active' | 'inactive' | 'error' | 'deploying'
  version: string
  recipes: MetaRecette[]
  createdAt: string
  updatedAt: string
  description?: string
  environment?: string
  deploymentKind?: DeploymentKind
  instanceCount?: number
  timeToLive?: number
  apiKey?: string
  allowMcpGateway?: boolean
  allowRag?: boolean
  allowOcr?: boolean
  allowMcpAdmin?: boolean
  allowDevops?: boolean
  allowDocstore?: boolean
  allowCompanyMemory?: boolean
  allowRecipes?: boolean
  allowLlmDirect?: boolean
  allowMultimodal?: boolean
  allowShell?: boolean
  allowMqttGateway?: boolean
  allowMessagingGateway?: boolean
  allowExternalInception?: boolean
  externalInceptionMode?: 'god' | 'readonly'
  isAdmin?: boolean
  instanceRole?: 'none' | 'chief_of_staff'
  storage?: StorageType
  systemPrompt?: string
  maxCompletionTokens?: number
  statusMessage?: string
  lastDeployedAt?: string
  configVersion?: number
  llmModelId?: string
  companyMemoryStatus?: {
    desired: boolean
    runtimeStatus: CompanyMemoryStatus['runtime_status']
    provider?: string
    sidecarId?: string
    message?: string
  }
  llmProviders?: LLMProvider[]
  mcpStdioServers?: MCPSTDIOServer[]
  mcpSseServers?: MCPSSEServer[]
  skills?: RegistrySkill[]
  skillIds?: Array<string | number>
  edges?: DeploymentEdges
}

export interface UpdateDeploymentResponse {
  deployment: Deployment
  needsRedeploy: boolean
  message: string
  companyMemoryStatus?: Deployment['companyMemoryStatus']
}

export interface RedeployDeploymentResponse {
  status?: string
  deployment_id?: string
  config_version?: number
  expected_completion?: string
}

/**
 * Real contract of GET /organization/{orgId}/deployment/{id}/status
 * (chatserver deployment_controller.GetDeploymentStatus). The previous shape
 * (health/uptime/lastCheck/executions) described fields the backend never sent.
 */
export interface DeploymentStatusComponent {
  name: string
  status: boolean
}

export interface DeploymentStatus {
  id: string
  status: string
  status_message?: string
  config_version?: number
  last_deployed_at?: string | null
  needs_redeploy?: boolean
  company_memory_status?: unknown
  components?: {
    llm?: DeploymentStatusComponent
    mcp_sse?: DeploymentStatusComponent[] | null
    mcp_stdio?: DeploymentStatusComponent[] | null
  }
}

export type DeploymentHealthLevel = 'healthy' | 'degraded' | 'unhealthy'

/** Derives an aggregate health level from the component statuses. */
export function deriveDeploymentHealth(status?: DeploymentStatus | null): {
  level?: DeploymentHealthLevel
  up: number
  total: number
} {
  const components: DeploymentStatusComponent[] = [
    ...(status?.components?.llm ? [status.components.llm] : []),
    ...(status?.components?.mcp_sse ?? []),
    ...(status?.components?.mcp_stdio ?? []),
  ]
  if (components.length === 0) {
    return { level: undefined, up: 0, total: 0 }
  }
  const up = components.filter((component) => component.status).length
  const level: DeploymentHealthLevel =
    up === components.length ? 'healthy' : up > 0 ? 'degraded' : 'unhealthy'
  return { level, up, total: components.length }
}

export interface OrganizationMember {
  id: string
  role: string
  joinedAt: string
  user: {
    id: string
    email?: string
    username?: string
    firstName?: string
    lastName?: string
  }
}

export interface DeploymentMember {
  id: string
  deploymentId: string
  userId: string
  role: 'operator' | 'viewer'
  assignedAt: string
  user: {
    id: string
    email?: string
    username?: string
    firstName?: string
    lastName?: string
  }
}

function mapMemberUser(raw: Record<string, any>) {
  const user = raw?.user ?? raw?.edges?.user ?? {}
  return {
    id: String(user?.id ?? raw?.user_id ?? raw?.userId ?? ''),
    email: user?.email,
    username: user?.username,
    firstName: user?.first_name ?? user?.firstName,
    lastName: user?.last_name ?? user?.lastName,
  }
}

function mapOrganizationMember(member: Record<string, any>): OrganizationMember {
  return {
    id: String(member?.id ?? ''),
    role: String(member?.role ?? 'member'),
    joinedAt: member?.joined_at ?? member?.joinedAt ?? new Date().toISOString(),
    user: mapMemberUser(member),
  }
}

function mapDeploymentMember(member: Record<string, any>): DeploymentMember {
  return {
    id: String(member?.id ?? ''),
    deploymentId: String(member?.deployment_id ?? member?.deploymentId ?? ''),
    userId: String(member?.user_id ?? member?.userId ?? member?.user?.id ?? ''),
    role: member?.role === 'viewer' ? 'viewer' : 'operator',
    assignedAt: member?.assigned_at ?? member?.assignedAt ?? new Date().toISOString(),
    user: mapMemberUser(member),
  }
}

function mapCompanyMemoryStatus(
  status: Record<string, any> | null | undefined,
): Deployment['companyMemoryStatus'] {
  if (!status || typeof status !== 'object') {
    return undefined
  }

  return {
    desired: Boolean(status.desired),
    runtimeStatus: status.runtime_status ?? status.runtimeStatus ?? 'disabled',
    provider: status.provider,
    sidecarId: status.sidecar_id ?? status.sidecarId,
    message: status.message,
  }
}

function mapDeployment(deployment: Record<string, any>): Deployment {
  return {
    id: String(deployment?.id ?? deployment?.deployment_id ?? ''),
    name: deployment?.name ?? deployment?.deployment_name ?? 'Agent',
    orgId: String(
      deployment?.organization_id ??
        deployment?.organizationId ??
        deployment?.orgId ??
        '',
    ),
    runtimeTarget:
      deployment?.runtime_target ??
      deployment?.runtimeTarget ??
      undefined,
    status: deployment?.status ?? 'active',
    version: deployment?.version ?? deployment?.deployment_version ?? '0.0.0',
    recipes: [],
    createdAt:
      deployment?.created_at ?? deployment?.createdAt ?? new Date().toISOString(),
    updatedAt:
      deployment?.updated_at ?? deployment?.updatedAt ?? new Date().toISOString(),
    description: deployment?.description,
    environment: deployment?.environment,
    deploymentKind: normalizeDeploymentKind(
      deployment?.deployment_kind ?? deployment?.deploymentKind,
    ),
    instanceCount: deployment?.instance_count ?? deployment?.instanceCount,
    timeToLive: deployment?.time_to_live ?? deployment?.timeToLive,
    apiKey: deployment?.apiKey ?? deployment?.api_key,
    allowMcpGateway: deployment?.allow_mcp_gateway ?? deployment?.allowMcpGateway,
    allowRag: deployment?.allow_rag ?? deployment?.allowRag,
    allowOcr: deployment?.allow_ocr ?? deployment?.allowOcr,
    allowMcpAdmin: deployment?.allow_mcp_admin ?? deployment?.allowMcpAdmin,
    allowDevops: deployment?.allow_devops ?? deployment?.allowDevops,
    allowDocstore: deployment?.allow_docstore ?? deployment?.allowDocstore,
    allowCompanyMemory:
      deployment?.allow_company_memory ?? deployment?.allowCompanyMemory,
    allowRecipes: deployment?.allow_recipes ?? deployment?.allowRecipes,
    allowLlmDirect: deployment?.allow_llm_direct ?? deployment?.allowLlmDirect,
    allowMultimodal: deployment?.allow_multimodal ?? deployment?.allowMultimodal,
    allowShell: deployment?.allow_shell ?? deployment?.allowShell,
    allowMqttGateway:
      deployment?.allow_mqtt_gateway ?? deployment?.allowMqttGateway,
    allowMessagingGateway:
      deployment?.allow_messaging_gateway ?? deployment?.allowMessagingGateway,
    allowExternalInception:
      deployment?.allow_external_inception ??
      deployment?.allowExternalInception,
    externalInceptionMode:
      deployment?.external_inception_mode ?? deployment?.externalInceptionMode,
    isAdmin: deployment?.is_admin ?? deployment?.isAdmin,
    instanceRole: readInstanceRole(
      deployment?.instance_role ?? deployment?.instanceRole,
    ),
    storage: deployment?.storage,
    systemPrompt: deployment?.system_prompt ?? deployment?.systemPrompt,
    maxCompletionTokens:
      deployment?.max_completion_tokens ?? deployment?.maxCompletionTokens,
    statusMessage: deployment?.status_message ?? deployment?.statusMessage,
    lastDeployedAt: deployment?.last_deployed_at ?? deployment?.lastDeployedAt,
    configVersion: deployment?.config_version ?? deployment?.configVersion,
    llmModelId:
      deployment?.llm_model_id ??
      deployment?.llmModelId ??
      deployment?.edges?.llmmodel?.id,
    companyMemoryStatus: mapCompanyMemoryStatus(
      deployment?.company_memory_status ?? deployment?.companyMemoryStatus,
    ),
    llmProviders:
      deployment?.llmProviders ?? deployment?.edges?.llmprovider ?? [],
    mcpStdioServers:
      deployment?.mcpStdioServers ?? deployment?.edges?.mcpstdioserver ?? [],
    mcpSseServers:
      deployment?.mcpSseServers ?? deployment?.edges?.mcpsseserver ?? [],
    skills:
      deployment?.skills ??
      deployment?.edges?.skills ??
      deployment?.edges?.skill ??
      [],
    skillIds: deployment?.skill_ids ?? deployment?.skillIds ?? [],
    edges: deployment?.edges,
  }
}

class DeploymentsAPI {
  /**
   * List deployments for an organization
   */
  async listDeployments(orgId: string): Promise<Deployment[]> {
    const response = await apiClient.get<Record<string, any>[]>(
      `/organization/${orgId}/deployment/list`,
    )
    return Array.isArray(response)
      ? response
          .map((item) => mapDeployment(item ?? {}))
          .filter(
            (item) =>
              item.isAdmin !== true && !isChiefOfStaffDeployment(item),
          )
      : []
  }

  /**
   * Get deployment details
   */
  async getDeployment(orgId: string, deploymentId: string): Promise<Deployment> {
    const response = await apiClient.get<Record<string, any>>(
      `/organization/${orgId}/deployment/${deploymentId}`,
    )
    return mapDeployment(response)
  }

  /**
   * Get deployment status
   */
  async getDeploymentStatus(
    orgId: string,
    deploymentId: string,
  ): Promise<DeploymentStatus> {
    return apiClient.get(
      `/organization/${orgId}/deployment/${deploymentId}/status`,
    )
  }

  async redeployDeployment(
    orgId: string,
    deploymentId: string,
    options?: { force?: boolean },
  ): Promise<RedeployDeploymentResponse> {
    return apiClient.post(
      `/organization/${orgId}/deployment/${deploymentId}/redeploy`,
      options || {},
    )
  }

  async listOrganizationMembers(orgId: string): Promise<OrganizationMember[]> {
    const response = await apiClient.get<Record<string, any>[] | { members?: Record<string, any>[] }>(
      `/organization/${orgId}/members`,
    )
    const members = Array.isArray(response) ? response : response?.members ?? []
    return members.map((item) => mapOrganizationMember(item ?? {}))
  }

  async listDeploymentMembers(orgId: string, deploymentId: string): Promise<DeploymentMember[]> {
    const response = await apiClient.get<{ members?: Record<string, any>[] }>(
      `/organization/${orgId}/deployment/${deploymentId}/members`,
    )
    return (response?.members ?? []).map((item) => mapDeploymentMember(item ?? {}))
  }

  async assignDeploymentMember(
    orgId: string,
    deploymentId: string,
    data: { user_id: string; role: DeploymentMember['role'] },
  ): Promise<DeploymentMember> {
    const response = await apiClient.post<{ member?: Record<string, any> }>(
      `/organization/${orgId}/deployment/${deploymentId}/members`,
      data,
    )
    return mapDeploymentMember(response?.member ?? {})
  }

  async removeDeploymentMember(
    orgId: string,
    deploymentId: string,
    memberId: string,
  ): Promise<void> {
    await apiClient.delete(`/organization/${orgId}/deployment/${deploymentId}/members/${memberId}`)
  }

  /**
   * Create a deployment
   */
  async createDeployment(
    orgId: string,
    data: {
      name: string
      description?: string
      environment?: string
      deployment_kind?: 'sandbox' | 'development' | 'integration_test' | 'staging' | 'production'
    },
  ): Promise<Deployment> {
    const response = await apiClient.post<Record<string, any>>(
      `/organization/${orgId}/deployment`,
      data,
    )
    return mapDeployment(response)
  }

  /**
   * Update deployment
   */
  async updateDeployment(
    orgId: string,
    deploymentId: string,
    data: Partial<Deployment> & UpdateInternalMCPRequest,
  ): Promise<UpdateDeploymentResponse> {
    return this.updateDeploymentRuntime(orgId, deploymentId, data as Record<string, any>)
  }

  async updateDeploymentRuntime(
    orgId: string,
    deploymentId: string,
    data: Record<string, any>,
  ): Promise<UpdateDeploymentResponse> {
    const response = await apiClient.put<Record<string, any>>(
      `/organization/${orgId}/deployment/${deploymentId}`,
      data,
    )
    return {
      deployment: mapDeployment(response?.deployment ?? response),
      needsRedeploy: Boolean(
        response?.needs_redeploy ?? response?.needsRedeploy ?? false,
      ),
      message: response?.message ?? '',
      companyMemoryStatus: mapCompanyMemoryStatus(
        response?.company_memory_status ?? response?.companyMemoryStatus,
      ),
    }
  }

  async getStorageCapabilities(): Promise<StorageCapabilities> {
    return apiClient.get('/runtime/storage-capabilities')
  }

  /**
   * Delete deployment
   */
  async deleteDeployment(orgId: string, deploymentId: string): Promise<void> {
    return apiClient.delete(
      `/organization/${orgId}/deployment/${deploymentId}`,
    )
  }

  exportSnapshot(
    orgId: string,
    deploymentId: string,
    data: DeploymentSnapshotExportRequest,
  ): Promise<DeploymentSnapshotEnvelope> {
    return apiClient.post(
      `/organization/${orgId}/deployment/${deploymentId}/export`,
      data,
    )
  }

  importSnapshotJSON(
    orgId: string,
    data: DeploymentSnapshotImportRequest,
  ): Promise<DeploymentSnapshotImportResult> {
    return apiClient.post(`/organization/${orgId}/deployment/import-json`, data)
  }
}

export const deploymentsAPI = new DeploymentsAPI()
